import {Card} from "primereact/card";
import {Toast} from "primereact/toast";
import type {ReactNode} from "react";
import {useRef} from "react";
import {useTranslation} from "react-i18next";

export function DetailsPanelSeparator() {
    return <hr className="my-4 border-surface-0 dark:border-surface-900 bg-gray-400" />;
}

interface DetailsPanelProps {
    /** optional icon to show in title */
    icon?: string;
    title: ReactNode;
    /** rendered panel content */
    children: ReactNode;
    /** optional (JSON) data to copy to clipboard, will show copy icon in title when set */
    dataToCopy?: string;
    /** optional content rendered right-aligned in the panel header */
    headerActions?: ReactNode;
}

/**
 * Details Panel.
 */
export function DetailsPanel({icon, title, children, dataToCopy, headerActions}: DetailsPanelProps) {
    const toastRef = useRef<Toast>(null);
    const {t} = useTranslation();

    const copyData = async () => {
        if (!dataToCopy) {
            return;
        }
        try {
            await navigator.clipboard.writeText(dataToCopy);
            toastRef.current?.show({
                severity: "info",
                summary: "Info",
                detail: t("object.copiedJsonToClipboard"),
            });
        } catch (e) {
            console.warn("Could not copy JSON to clipboard", e);
        }
    };

    return (
        <Card className="InfoCard bg-surface-100 dark:bg-surface-700">
            <div className="font-bold text-lg mb-4 pb-2 border-b border-gray-400 flex items-center">
                <span>
                    {icon && <span className={`${icon} mr-2`} />}
                    {title}
                    {!!dataToCopy && (
                        <span className="pi pi-copy text-primary ml-2 cursor-pointer" onClick={() => void copyData()} />
                    )}
                </span>
                {headerActions && <span className="ml-auto InfoCardHeaderActions">{headerActions}</span>}
            </div>
            {children}
            <Toast ref={toastRef} position="top-center" />
        </Card>
    );
}

interface DetailsPanelRowProps<V extends ReactNode> {
    /** optional icon to display in row (use "" to render empty icon placeholder for alignment) */
    icon?: string | null;
    /** row label; omit it for a row that carries only an icon and a value */
    label?: string;
    /** value to render, or to determine if the row is rendered at all (requires truthy value to render the row) */
    value?: V;
    /** optional classname override (default: "mb-1") */
    className?: string;
    /** optional classname for the value */
    valueClassName?: string;
    /** optional render function for the value; only called with the truthy value that made the row render */
    renderValue?: (value: NonNullable<V>) => ReactNode;
}

/**
 * Optional row.
 */
export function DetailsPanelRow<V extends ReactNode>({
    icon,
    label,
    value,
    className,
    valueClassName,
    renderValue,
}: DetailsPanelRowProps<V>) {
    if (!value) return null;
    const hasIcon = !!icon || icon === "";
    return (
        <div
            key={label}
            className={`OrderDetailsRow ${hasIcon ? "OrderDetailsRowWithIcon " : ""}${className ?? "mb-1"}`}
        >
            {icon ? (
                <div className="Icon">
                    <span className={icon} />
                </div>
            ) : icon === "" ? (
                <div className="Icon" />
            ) : null}
            <div className="Label">{label}</div>
            <div className={`Value${valueClassName ? ` ${valueClassName}` : ""}`}>
                {typeof renderValue === "function" ? renderValue(value) : value}
            </div>
        </div>
    );
}
